import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { ListItemButton, ListItemText } from "@mui/material";
import DragPreviewLayer from "designSystem/dnd/components/DragPreviewLayer";
import { DndMode, DndStore, DndStoreContext, DragAndColumnDropWrapper } from "designSystem/dnd/index";
import { useSyncLocalStore } from "designSystem/hooks";
import { Box, FlexColumn, FlexRowCenter, IconButton, OverflowText } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { FC, ReactElement, useRef } from "react";
import { useCustomViewDialogStore } from "../CustomViewDialogStore";
import CustomViewFavoriteButton from "../CustomViewFavoriteButton";
import css from "./CustomViewDialogFavorites.module.css";
import { CategoryIconMap } from "./CustomViewDialogLeftPanelAccordion";
import { CustomViewItem } from "./CustomViewItem";
import CustomViewItemActionMenuPartial from "./CustomViewItemActionMenuPartial";

const DRAG_DROP_TYPE = "CustomViewDialogFavorites";

const AccordionItemPreviewComponent = (item: CustomViewItem): ReactElement => (<FavoriteCustomViewItem item={item} />);

export const CustomViewDialogFavorites: FC<{}> = observer(() => {
	const {
		customViewStore: {
			dropFavorite,
			favoriteItems,
			filteredFavoriteItems,
			isLoadingFavorites,
		},
	} = useCustomViewDialogStore();
	const hasFavorite = favoriteItems.length > 0;

	const dndStore = useSyncLocalStore(() => new DndStore<CustomViewItem>());

	return (
		<>
			{hasFavorite && (
				<FlexRowCenter
					data-csi-automation="CustomViewDialogFavorites"
					pl={5}
					py={2}
				>
					<FlexColumn width={1}>
						<OverflowText
							bold
							color="grey.A100"
							pb={2}
							res="Favorites"
							uppercase
						/>
						<DndStoreContext.Provider value={dndStore}>
							<DragAndColumnDropWrapper<CustomViewItem>
								canDrag
								disableBgColorChangeOnDrag
								disableDragWhileLoading
								dragType={DRAG_DROP_TYPE}
								dropTypes={[DRAG_DROP_TYPE]}
								forceLoadingState={isLoadingFavorites}
								isFullScreen
								isRootDropWrapper
								list={filteredFavoriteItems}
								mode={DndMode.Normal}
								onDrop={dropFavorite}
								renderItem={(item: CustomViewItem) => <FavoriteCustomViewItem item={item} />}
							/>
							<DragPreviewLayer previewComponent={AccordionItemPreviewComponent} />
						</DndStoreContext.Provider>
					</FlexColumn>
				</FlexRowCenter>
			)}
		</>
	);
});

interface FavoriteCustomViewItemProps {
	/** favorite item */
	item: CustomViewItem;
}

const FavoriteCustomViewItem: FC<FavoriteCustomViewItemProps> = observer((props) => {
	const { item } = props;
	const { category, name, select, selected, url } = item;
	const hoverRef = useRef(null);

	return (
		<ListItemButton
			className={css.list}
			data-csi-automation="FavoriteCustomViewItem"
			data-csi-custom-view={url}
			key={url}
			onClick={select}
			ref={hoverRef}
			selected={selected}
		>
			<FlexRowCenter
				flexGrow={1}
				gap={2}
				justifyContent="space-between"
			>
				<FlexRowCenter gap={2}>
					<IconButton
						className={css.showOnHover}
						design="dragHandle"
						size="small"
					>
						<DragIndicatorIcon />
					</IconButton>
					<Box>{CategoryIconMap[category]}</Box>
					<ListItemText primary={name} />
					<CustomViewFavoriteButton customViewItem={item} design="defaultOnGrey" />
				</FlexRowCenter>
			</FlexRowCenter>
		</ListItemButton>
	);
});